import { useEffect, useMemo, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { MaskedLines, FadeUp } from "../components/Reveal";
import { Slider } from "../components/ui/slider";
import { api, formatApiErrorDetail } from "../lib/api";

/* --------------------------------- Client-side simulator ------------------ */

function runLocal(state) {
    const utilGain = state.utilization > 9 ? Math.min(45, Math.round((state.utilization - 9) * 0.55)) : 0;
    const lateGain = Math.min(120, state.late_payments * 14);
    const collGain = Math.min(180, state.collections * 35);
    const inqGain = Math.min(30, state.hard_inquiries * 4);
    const idGain = Math.min(160, state.identity_theft_items * 40);
    const rawTotal = utilGain + lateGain + collGain + inqGain + idGain;
    const room = Math.max(0, 850 - state.current_score);
    const dampened = Math.min(rawTotal, Math.round(room * 0.9));
    const projected = Math.min(850, state.current_score + dampened);
    const guaranteedDelta = rawTotal >= 30 ? 93 : Math.min(rawTotal, 30);
    const guaranteed = Math.min(850, state.current_score + guaranteedDelta);
    return {
        projected,
        delta: projected - state.current_score,
        guaranteed,
        guaranteedDelta,
        breakdown: [
            { factor: "Utilization optimized", gain: utilGain },
            { factor: "Late payments removed", gain: lateGain },
            { factor: "Collections removed", gain: collGain },
            { factor: "Hard inquiries removed", gain: inqGain },
            { factor: "Identity theft items resolved", gain: idGain },
        ],
    };
}

function scoreBand(s) {
    if (s >= 800) return { label: "Exceptional", tone: "text-accent" };
    if (s >= 740) return { label: "Very Good", tone: "text-accent" };
    if (s >= 670) return { label: "Good", tone: "text-foreground" };
    if (s >= 580) return { label: "Fair", tone: "text-foreground" };
    return { label: "Poor", tone: "text-destructive" };
}

/* ------------------------- Animated number component --------------------- */

function AnimatedNumber({ value, className = "" }) {
    const mv = useMotionValue(value);
    const spring = useSpring(mv, { stiffness: 120, damping: 22 });
    const rounded = useTransform(spring, (v) => Math.round(v));
    useEffect(() => {
        mv.set(value);
    }, [value, mv]);
    return <motion.span className={className}>{rounded}</motion.span>;
}

/* -------------------------------- SliderRow ------------------------------- */

function SliderRow({ label, value, min, max, step = 1, unit, onChange, testId, help }) {
    return (
        <div className="border-t hairline py-6" data-testid={testId}>
            <div className="flex items-baseline justify-between gap-4 mb-4">
                <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                        {label}
                    </div>
                    {help && <div className="mt-1 text-xs text-muted-foreground/80">{help}</div>}
                </div>
                <div className="font-editorial text-4xl tracking-tighter tabular-nums">
                    {value}
                    {unit && <span className="text-lg text-muted-foreground align-baseline ml-1">{unit}</span>}
                </div>
            </div>
            <Slider
                min={min}
                max={max}
                step={step}
                value={[value]}
                onValueChange={(v) => onChange(v[0])}
                className="[&_[role=slider]]:rounded-none [&_[role=slider]]:border-2 [&_[role=slider]]:border-foreground [&_[role=slider]]:bg-background [&>span:first-child]:h-[2px] [&>span:first-child]:rounded-none [&_[data-orientation=horizontal]>span]:bg-accent"
            />
        </div>
    );
}

/* --------------------------------- Page ---------------------------------- */

export default function Simulator() {
    const [state, setState] = useState({
        current_score: 585,
        utilization: 68,
        late_payments: 3,
        collections: 2,
        hard_inquiries: 4,
        identity_theft_items: 0,
    });
    const [captured, setCaptured] = useState(false);
    const [lead, setLead] = useState({ name: "", email: "" });
    const [saving, setSaving] = useState(false);

    const result = useMemo(() => runLocal(state), [state]);
    const band = scoreBand(result.projected);
    const totalRaw = result.breakdown.reduce((a, b) => a + b.gain, 0);

    const saveLead = async (e) => {
        e.preventDefault();
        if (!lead.email) return;
        setSaving(true);
        try {
            await api.post("/simulator", {
                ...state,
                email: lead.email,
                name: lead.name || null,
            });
            setCaptured(true);
            toast.success("Your personalized plan is on its way.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const set = (k) => (v) => setState((s) => ({ ...s, [k]: v }));

    return (
        <div className="bg-background text-foreground min-h-screen">
            <Nav />

            {/* Hero */}
            <section className="pt-28 md:pt-36 pb-16 grain">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10">
                    <div className="grid grid-cols-12 gap-6 items-end">
                        <div className="col-span-12 md:col-span-8">
                            <div className="flex items-center gap-3 mb-8 text-[11px] tracking-[0.24em] uppercase text-muted-foreground">
                                <span className="w-8 h-px bg-muted-foreground" />
                                <Sparkle size={12} strokeWidth={1.5} />
                                <span>Interactive · Free · No credit pull</span>
                            </div>
                            <MaskedLines
                                as="h1"
                                className="font-editorial tracking-tighter text-[12vw] md:text-[8.6vw] lg:text-[7.4vw] leading-[0.86]"
                                lines={[
                                    "Score",
                                    <>
                                        <span className="italic text-accent">simulator</span>.
                                    </>,
                                ]}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-4 md:text-right">
                            <FadeUp delay={0.6}>
                                <p className="text-base text-muted-foreground max-w-sm md:ml-auto leading-relaxed">
                                    Move the sliders. Watch what happens when Access clears the
                                    items dragging your file down.
                                </p>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>

            {/* Simulator */}
            <section className="pb-24" data-testid="simulator-section">
                <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6">
                    {/* Inputs */}
                    <div className="col-span-12 lg:col-span-7 bg-card border hairline p-6 md:p-10" data-testid="simulator-inputs">
                        <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-2">
                            Your file today
                        </div>
                        <div className="font-editorial text-4xl md:text-5xl tracking-tighter">
                            Where you <span className="italic text-accent">stand.</span>
                        </div>

                        <SliderRow
                            label="Current credit score"
                            value={state.current_score}
                            min={300}
                            max={850}
                            onChange={set("current_score")}
                            testId="sim-current-score"
                        />
                        <SliderRow
                            label="Credit utilization"
                            value={state.utilization}
                            min={0}
                            max={100}
                            unit="%"
                            onChange={set("utilization")}
                            testId="sim-utilization"
                            help="Balance ÷ total limit across all cards. Aim for under 10%."
                        />
                        <SliderRow
                            label="Late payments (30-120 days)"
                            value={state.late_payments}
                            min={0}
                            max={12}
                            onChange={set("late_payments")}
                            testId="sim-late"
                        />
                        <SliderRow
                            label="Collections"
                            value={state.collections}
                            min={0}
                            max={10}
                            onChange={set("collections")}
                            testId="sim-collections"
                        />
                        <SliderRow
                            label="Hard inquiries (last 2 yrs)"
                            value={state.hard_inquiries}
                            min={0}
                            max={15}
                            onChange={set("hard_inquiries")}
                            testId="sim-inquiries"
                        />
                        <SliderRow
                            label="Identity theft items"
                            value={state.identity_theft_items}
                            min={0}
                            max={10}
                            onChange={set("identity_theft_items")}
                            testId="sim-identity"
                        />
                    </div>

                    {/* Result */}
                    <aside className="col-span-12 lg:col-span-5 space-y-4" data-testid="simulator-result">
                        {/* Big projected card */}
                        <div className="bg-foreground text-background p-8 md:p-10 relative overflow-hidden">
                            <div className="text-[11px] tracking-[0.24em] uppercase text-background/60 mb-6">
                                Projected FICO after Access
                            </div>
                            <div className="flex items-baseline gap-4">
                                <div className="font-editorial text-[140px] md:text-[168px] leading-[0.86] tracking-tighter tabular-nums">
                                    <AnimatedNumber value={result.projected} />
                                </div>
                                <div className="pb-4">
                                    <div className={`font-editorial text-3xl italic ${band.tone === "text-destructive" ? "text-red-300" : "text-accent"}`}>
                                        {band.label}
                                    </div>
                                    <div className="mt-1 font-mono-ui text-xs tracking-[0.16em] uppercase text-background/60">
                                        +<AnimatedNumber value={result.delta} /> points
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8 border-t border-background/20 pt-6 flex items-center justify-between gap-4">
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-background/60">
                                        Guaranteed minimum
                                    </div>
                                    <div className="font-editorial text-3xl mt-1">
                                        <AnimatedNumber value={result.guaranteed} />{" "}
                                        <span className="text-accent italic text-2xl">
                                            (+{result.guaranteedDelta})
                                        </span>
                                    </div>
                                </div>
                                <ShieldCheck size={28} strokeWidth={1.4} className="text-accent" />
                            </div>
                        </div>

                        {/* Breakdown */}
                        <div className="bg-card border hairline p-6 md:p-8" data-testid="simulator-breakdown">
                            <div className="text-[11px] tracking-[0.24em] uppercase text-muted-foreground mb-4">
                                Where the points come from
                            </div>
                            <ul className="space-y-3">
                                {result.breakdown.map((b) => {
                                    const pct = totalRaw ? Math.max(4, Math.round((b.gain / totalRaw) * 100)) : 0;
                                    return (
                                        <li key={b.factor} className="grid grid-cols-12 gap-3 items-center">
                                            <div className="col-span-6 text-sm">{b.factor}</div>
                                            <div className="col-span-4">
                                                <div className="h-[2px] bg-border relative">
                                                    <motion.div
                                                        className="absolute inset-y-0 left-0 bg-accent"
                                                        animate={{ width: `${b.gain ? pct : 0}%` }}
                                                        transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-span-2 text-right font-mono-ui text-xs tabular-nums">
                                                +{b.gain}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        {/* Lead capture / CTA */}
                        {!captured ? (
                            <form
                                onSubmit={saveLead}
                                className="bg-accent text-accent-foreground p-6 md:p-8"
                                data-testid="simulator-lead-form"
                            >
                                <div className="text-[11px] tracking-[0.24em] uppercase text-accent-foreground/70 mb-2">
                                    Get this in writing
                                </div>
                                <div className="font-editorial text-3xl tracking-tighter leading-tight">
                                    Send my personalized plan.
                                </div>
                                <div className="mt-5 space-y-3">
                                    <input
                                        placeholder="First name"
                                        value={lead.name}
                                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                                        data-testid="sim-lead-name"
                                        className="w-full bg-transparent border-b border-accent-foreground/40 focus:border-accent-foreground py-2 font-mono-ui text-sm outline-none placeholder:text-accent-foreground/50"
                                    />
                                    <input
                                        type="email"
                                        required
                                        placeholder="Email"
                                        value={lead.email}
                                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                                        data-testid="sim-lead-email"
                                        className="w-full bg-transparent border-b border-accent-foreground/40 focus:border-accent-foreground py-2 font-mono-ui text-sm outline-none placeholder:text-accent-foreground/50"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="sim-lead-submit"
                                    className="mt-6 group w-full inline-flex items-center justify-between bg-background text-foreground px-5 py-4 text-[12px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background disabled:opacity-60"
                                >
                                    {saving ? "Sending…" : "Send my plan"}
                                    <ArrowUpRight
                                        size={16}
                                        className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </button>
                            </form>
                        ) : (
                            <div className="bg-accent text-accent-foreground p-6 md:p-8" data-testid="simulator-lead-success">
                                <div className="font-editorial text-3xl tracking-tighter">
                                    On its way.
                                </div>
                                <p className="mt-3 text-sm text-accent-foreground/85">
                                    We&rsquo;ll email a personalized plan and follow up within one business day.
                                    Want to talk sooner?
                                </p>
                                <Link
                                    to="/#consult"
                                    className="mt-6 group inline-flex items-center gap-2 bg-background text-foreground px-5 py-3 text-[12px] tracking-[0.16em] uppercase hover:bg-foreground hover:text-background"
                                >
                                    Book a call
                                    <ArrowUpRight size={14} />
                                </Link>
                            </div>
                        )}

                        <p className="font-mono-ui text-[10px] tracking-[0.16em] uppercase text-muted-foreground pt-2">
                            Estimates only. Not a credit pull. Not a guarantee of specific results
                            beyond the written 93-point program guarantee.
                        </p>
                    </aside>
                </div>
            </section>

            <Footer />
        </div>
    );
}
